import { useEffect, useRef } from 'react';
import { Search } from 'lucide-react';
import { useMapsLibrary, useMap } from '@vis.gl/react-google-maps';

interface PlaceSearchInputProps {
  /** mapId of the <Map> instance the search should pan/zoom. Must match the
   *  `mapId` prop on the sibling <Map>. */
  mapId: string;
  /** Called when the user picks a suggestion. */
  onPlaceSelect: (lat: number, lng: number) => void;
  placeholder?: string;
  /** ISO country code restriction (default 'gt'). */
  country?: string;
  className?: string;
}

/**
 * Google Places Autocomplete input that pans the parent <Map> on selection.
 * Uses the legacy Places API (already enabled on this project).
 */
export function PlaceSearchInput({
  mapId,
  onPlaceSelect,
  placeholder = 'Buscar dirección o lugar en Guatemala...',
  country = 'gt',
  className = '',
}: PlaceSearchInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const callbackRef = useRef(onPlaceSelect);
  callbackRef.current = onPlaceSelect;
  const placesLib = useMapsLibrary('places');
  const map = useMap(mapId);

  useEffect(() => {
    if (!placesLib || !inputRef.current) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const ac = new (placesLib as any).Autocomplete(inputRef.current, {
      fields: ['geometry'],
      componentRestrictions: { country },
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const listener = (window as any).google.maps.event.addListener(ac, 'place_changed', () => {
      const place = ac.getPlace();
      if (place.geometry?.location) {
        const lat: number = place.geometry.location.lat();
        const lng: number = place.geometry.location.lng();
        callbackRef.current(lat, lng);
        if (map) {
          map.panTo({ lat, lng });
          map.setZoom(17);
        }
      }
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return () => (window as any).google.maps.event.removeListener(listener);
  }, [placesLib, map, country]);

  return (
    <div className={`relative mb-2 ${className}`}>
      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none z-10" />
      <input
        ref={inputRef}
        type="text"
        placeholder={placeholder}
        className="w-full pl-8 pr-3 py-2 text-sm rounded-md border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
      />
    </div>
  );
}
